package com.evoapps.evoshift.data.dto.courses

import kotlinx.serialization.EncodeDefault
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.Serializable

/*
 * SH-126 documents (Courses.kt says who reads what). firestore.rules (validCourse,
 * validCourseKey, ownCourseProgress, the assignment rule) allow only these fields; times are
 * ISO-8601 UTC, days "YYYY-MM-DD". Results, OHS records and training statuses are written by
 * functions/courses.js only: their DTOs list what the app reads and ignore the rest.
 */

/**
 * SH-211 added [rich] (the formatted lesson as RichText's versioned JSON, a string) and
 * [presentation]. Older app versions decode pages without them (GitLive's decoder reads only the
 * fields its class knows) and show [body], the plain-text version, so they keep working. Both are
 * left out when null: firestore.rules checks rich's length on every page and has no expressions
 * to spare for a null.
 */
@OptIn(ExperimentalSerializationApi::class)
@Serializable
data class CoursePageDto(
    val id: String = "",
    val kind: String = "TEXT",
    val title: String = "",
    val body: String = "",
    val imagePath: String? = null,
    val url: String? = null,
    @EncodeDefault(EncodeDefault.Mode.NEVER) val rich: String? = null,
    @EncodeDefault(EncodeDefault.Mode.NEVER) val presentation: CoursePresentationDto? = null
)

/** SH-211: a page's presentation (Presentations.kt). */
@Serializable
data class CoursePresentationDto(
    val source: String = "UPLOAD",
    val link: String? = null,
    val embedUrl: String? = null,
    val filePath: String? = null,
    val fileName: String? = null,
    val fileType: String? = null,
    val pdfPath: String? = null,
    val pdfName: String? = null
)

@Serializable
data class QuizQuestionDto(
    val id: String = "",
    val text: String = "",
    val options: List<String> = emptyList(),
    val multi: Boolean = false
)

@Serializable
data class OhsCourseInfoDto(
    val lessonHours: Int = 0,
    val instructorName: String = "",
    val consultation: String = ""
)

/** workplaces/{wpId}/courses/{courseId}. */
@Serializable
data class CourseDto(
    val id: String = "",
    val workplaceId: String = "",
    val title: String = "",
    val description: String = "",
    val kind: String = "GENERAL",
    val pages: List<CoursePageDto> = emptyList(),
    val questions: List<QuizQuestionDto> = emptyList(),
    val passPercent: Int = 80,
    val roles: List<String> = emptyList(),
    val allNewHires: Boolean = false,
    val dueDays: Int = 14,
    val countsForReadiness: Boolean = false,
    val ohs: OhsCourseInfoDto? = null,
    val version: Int = 0,
    val status: String = "DRAFT",
    val publishedAt: String? = null,
    val updatedAt: String? = null,
    val updatedBy: String? = null
)

/** workplaces/{wpId}/course_keys/{courseId}: managers only. */
@Serializable
data class CourseKeyDto(
    val courseId: String = "",
    val version: Int = 0,
    val correct: Map<String, List<Int>> = emptyMap(),
    val updatedAt: String? = null,
    val updatedBy: String? = null
)

/** workplaces/{wpId}/courses/{courseId}/assignments/{uid}. */
@Serializable
data class CourseAssignmentDto(
    val userId: String = "",
    val courseId: String = "",
    val dueOn: String? = null,
    val assignedAt: String = "",
    val assignedBy: String = ""
)

/** workplaces/{wpId}/course_progress/{uid}_{courseId}: the person's own. */
@Serializable
data class CourseProgressDto(
    val userId: String = "",
    val courseId: String = "",
    val workplaceId: String = "",
    val pagesSeen: List<String> = emptyList(),
    val startedAt: String? = null,
    val updatedAt: String? = null
)

/** workplaces/{wpId}/courses/{courseId}/completions/{uid}: server only. */
@Serializable
data class CourseCompletionDto(
    val userId: String = "",
    val courseId: String = "",
    val version: Int = 0,
    val score: Int = 0,
    val passed: Boolean = false,
    val attempts: Int = 0,
    val attemptsToday: Int = 0,
    val attemptDay: String? = null,
    val completedAt: String? = null,
    val lastAttemptAt: String? = null,
    val displayName: String? = null
)

@Serializable
data class OhsGeneralDto(
    val mode: String = "IN_PERSON",
    val conductedBy: String? = null,
    val completedOn: String? = null,
    val courseId: String? = null,
    val courseVersion: Int? = null,
    val courseTitle: String? = null,
    val score: Int? = null,
    val employeeConfirmedAt: String? = null,
    val recordedByName: String? = null
)

@Serializable
data class OhsStationDto(
    val position: String = "",
    val conductedBy: String = "",
    val conductedOn: String? = null,
    val admittedOn: String? = null,
    val recordedByName: String? = null,
    val recordedAt: String? = null,
    val employeeConfirmedAt: String? = null
)

@Serializable
data class OhsPeriodicDto(
    val mode: String = "SELF_STUDY",
    val courseId: String? = null,
    val courseVersion: Int? = null,
    val courseTitle: String? = null,
    val score: Int? = null,
    val testPassedOn: String? = null,
    val examOn: String? = null,
    val commission: String? = null,
    val certificateNumber: String? = null,
    val organizer: String? = null,
    val recordedByName: String? = null
)

/** workplaces/{wpId}/ohs_records/{uid}: server only. */
@Serializable
data class OhsRecordDto(
    val userId: String = "",
    val workplaceId: String = "",
    val displayName: String? = null,
    val group: String? = null,
    val stationRequired: Boolean = true,
    val general: OhsGeneralDto? = null,
    val station: OhsStationDto? = null,
    val periodic: OhsPeriodicDto? = null,
    val updatedAt: String? = null
)

/** workplaces/{wpId}/training_status/{uid}: server only. */
@Serializable
data class TrainingStatusDto(
    val userId: String = "",
    val workplaceId: String = "",
    val state: String = "PENDING",
    val done: Int = 0,
    val pending: Int = 0,
    val dueOn: String? = null
)
